import React from "react";
import SEO from "../components/SEO";
import "../styles/Company.css";
import { useScrollReveal } from "../hooks/useScrollReveal";

const team = [
  {
    name: "Product Lead",
    role: "CEO & Co-founder",
    initials: "PL",
    gradient: "linear-gradient(135deg, #1475ff, #14c9b2)",
    bio: "Product strategist and engineer with a passion for building high-retention mobile experiences.",
  },
  {
    name: "Design Lead",
    role: "Head of UX/UI",
    initials: "DL",
    gradient: "linear-gradient(135deg, #f37498, #fb897e)",
    bio: "Crafts user flows that balance aesthetics with conversion — from wireframes to pixel-perfect delivery.",
  },
  {
    name: "Engineering Lead",
    role: "Head of Engineering",
    initials: "EL",
    gradient: "linear-gradient(135deg, #8e54e9, #4776e6)",
    bio: "Leads backend architecture, CI/CD pipelines, and cross-platform mobile delivery.",
  },
  {
    name: "Growth Lead",
    role: "Head of LiveOps",
    initials: "GL",
    gradient: "linear-gradient(135deg, #f7971e, #ffd200)",
    bio: "Drives post-launch analytics, A/B testing, and monetization strategy for all products.",
  },
];

const principles = [
  {
    title: "Product Ownership",
    text: "We align every feature with clear goals, user impact, and measurable outcomes.",
  },
  {
    title: "Engineering Quality",
    text: "Maintainable architecture, test-focused workflows, and release confidence by design.",
  },
  {
    title: "Execution Speed",
    text: "Fast iteration cycles with transparent milestones and predictable delivery.",
  },
];

const timeline = [
  { year: "2019", detail: "MacroMatrix Private Limited established as a mobile product company." },
  { year: "2021", detail: "Expanded into game production and LiveOps execution." },
  { year: "2023", detail: "Scaled cross-functional delivery for international partners." },
  { year: "Today", detail: "Focused on high-retention mobile products and growth systems." },
];

function Company() {
  useScrollReveal();
  return (
    <div className="company-container">
      <SEO title="About Us" description="MacroMatrix Private Limited is a Pakistan and Dubai-based digital product company specializing in mobile applications, games, and LiveOps." path="/company" />
      <section className="company-hero">
        <div className="company-text">
          <h2>About MacroMatrix</h2>
          <p>
            MacroMatrix Private Limited is a Pakistan-based digital product company specializing
            in mobile applications and games. We partner with startups and
            enterprises to design, build, and scale products that deliver
            measurable business results.
          </p>
        </div>
      </section>

      <section className="company-details reveal">
        <article className="company-block">
          <h3>Our Mission</h3>
          <p>
            Build high-quality mobile experiences through practical strategy,
            strong engineering, and long-term product thinking.
          </p>
        </article>
        <article className="company-block">
          <h3>What We Deliver</h3>
          <p>
            Product discovery, UX/UI design, development, backend integration,
            QA, store launch, and post-release optimization.
          </p>
        </article>
        <article className="company-block">
          <h3>Engagement Model</h3>
          <p>
            Transparent milestones, shared priorities, and weekly progress
            visibility from planning to release.
          </p>
        </article>
      </section>

      <section className="company-team reveal">
        <h3>The Team</h3>
        <p className="team-subtitle">A focused group of builders, designers, and growth operators.</p>
        <div className="team-grid">
          {team.map((member) => (
            <article key={member.name} className="team-card">
              <div className="team-avatar" style={{ background: member.gradient }}>
                {member.initials}
              </div>
              <h4>{member.name}</h4>
              <span className="team-role">{member.role}</span>
              <p>{member.bio}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="company-principles reveal">
        <h3>How We Operate</h3>
        <div className="principle-grid">
          {principles.map((item) => (
            <article key={item.title} className="principle-card">
              <h4>{item.title}</h4>
              <p>{item.text}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="company-timeline reveal">
        <h3>Studio Milestones</h3>
        <div className="timeline-list">
          {timeline.map((item) => (
            <div key={item.year} className="timeline-item">
              <span>{item.year}</span>
              <p>{item.detail}</p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

export default Company;
